<script setup lang="ts">
  import { onMounted, ref, nextTick, onBeforeUnmount, computed } from 'vue';
  import type { ProductVariantPickerResource, ProductVariantSimpleResource } from '@storefront/resources';

  interface Props {
    variant: ProductVariantPickerResource,
    variants?: ProductVariantSimpleResource[],
    series?: ProductVariantSimpleResource[],
    show: 'series' | 'variants',
  }

  interface Emits {
    (e: 'selected', variant: ProductVariantSimpleResource): void,
  }

  const emit = defineEmits<Emits>();
  const props = defineProps<Props>();

  const wrapperHtml = ref();
  const innerHtml = ref();
  const isOverflown = ref(false);
  const open = ref(false);
  const height = ref();
  const size = ref('40px');

  const elements = computed(() => {
    if (props.show === 'series') {
      return props.series || [];
    } else {
      return props.variants || [];
    }
  });

  function onSelected(variant: ProductVariantSimpleResource) {
    emit('selected', variant);
  }

  function toggle() {
    open.value = !open.value;
  }

  function resize() {
    let card = wrapperHtml.value.querySelector('.variants-grid-card') as HTMLElement;
    let rect = card.getBoundingClientRect();

    height.value = wrapperHtml.value.getBoundingClientRect().height + 'px';
    size.value = rect.width + 'px';

    nextTick(() => {
      setTimeout(() => {
        isOverflown.value = innerHtml.value.scrollHeight > innerHtml.value.clientHeight;
      }, 200);
    });
  }

  onMounted(() => {
    window.addEventListener('resize', resize, { passive: true });
    resize();
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', resize);
  });
</script>
<template>
  <div class="variants-grid">
    <h2 class="variants-grid-header" v-if="$slots.title">
      <slot name="title" />
    </h2>
    <div class="variants-grid__container" :class="{ 'variants-grid__container--expanded': open }">
      <div ref="innerHtml" class="variants-grid__inner">
        <div ref="wrapperHtml" class="variants-grid__wrapper">
          <div v-for="element in elements" class="variants-grid-card" :class="{ 'variants-grid-card--active': element.slug === variant.slug }" role="button" tabindex="0" :title="element.title || undefined" @click="onSelected(element)">
            <MediaPicture :photo="element.photo" sizes="240x240 2x, 120x120" />
          </div>
        </div>
      </div>
      <div v-if="isOverflown" class="variants-grid-show-more" role="button" tabindex="0" @click="toggle">
        {{ open ? $t('SHOP.CART.ACTION.SHOW_LESS') : $t('SHOP.CART.ACTION.SHOW_MORE') }}
      </div>
    </div>
  </div>
</template>
<style lang="scss">

  :root {
    --sf-variants-grid-gap: 3px;

    --sf-variants-grid-card-padding: 1px;
    --sf-variants-grid-card-radius: 0;
    --sf-variants-grid-card-border: var(--secondary-border);
    --sf-variants-grid-card-border-hover: var(--secondary-border-hover);
    --sf-variants-grid-card-border-active: var(--primary-border);
    --sf-variants-grid-card-border-active-hover: var(--primary-border-hover);

    --sf-variants-grid-more-foreground: var(--secondary-foreground);
    --sf-variants-grid-more-foreground-hover: var(--secondary-foreground-hover);
    --sf-variants-grid-more-background: var(--secondary-background);
    --sf-variants-grid-more-background-hover: var(--secondary-background-hover);
    --sf-variants-grid-more-border: var(--secondary-border);
    --sf-variants-grid-more-border-hover: var(--secondary-border-hover);
    --sf-variants-grid-more-border-radius: 0;
  }

  .variants-grid {
    --card-size: v-bind(size);
    --grid-height: v-bind(height);

    display: flex;
    width: 100%;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 0.5rem;

    &__container {
      display: grid;
      grid-template-rows: min(var(--grid-height), calc((var(--card-size) * 2) + var(--sf-variants-grid-gap)));
      transition: grid-template-rows 200ms ease-in-out;
      position: relative;
      width: 100%;

      &--expanded {
        grid-template-rows: calc(var(--grid-height) + var(--card-size) + var(--sf-variants-grid-gap));
      }
    }

    &__inner {
      overflow: hidden;
      min-height: 0;
    }

    &__wrapper {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(40px, 1fr));
      gap: var(--sf-variants-grid-gap);
      width: 100%;
      min-height: 0;
    }
  }

  .variants-grid-header {
    font-size: 1rem;
    font-weight: var(--font-weight-bold);
  }

  .variants-grid-card {
    display: flex;
    aspect-ratio: 1;
    padding: var(--sf-variants-grid-card-padding);
    transition: var(--primary-transition);
    border: 1px solid var(--sf-variants-grid-card-border);
    border-radius: var(--sf-variants-grid-card-radius);
    cursor: pointer;

    @media(hover: hover) {
      &:hover {
        border-color: var(--sf-variants-grid-card-border-hover);
      }
    }

    &--active {
      border-color: var(--sf-variants-grid-card-border-active);

      @media(hover: hover) {
        &:hover {
          border-color: var(--sf-variants-grid-card-border-active-hover);
        }
      }
    }

    picture,
    img {
      display: flex;
      object-fit: contain;
      object-position: center;
      aspect-ratio: 1;
    }
  }

  .variants-grid-show-more {
    position: absolute;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--sf-variants-grid-more-foreground);
    background: var(--sf-variants-grid-more-background);
    border: 1px solid var(--sf-variants-grid-more-border);
    border-radius: var(--sf-variants-grid-more-border-radius);
    transition-property: color, background-color, border-color;
    transition-timing-function: ease-in-out;
    transition-duration: 200ms;
    width: calc((var(--card-size) * 3) + (var(--sf-variants-grid-gap) * 2));
    height: var(--card-size);
    font-size: 0.75rem;
    font-weight: var(--font-weight-bold);
    cursor: pointer;

    @media(hover: hover) {
      &:hover {
        color: var(--sf-variants-grid-more-foreground-hover);
        background: var(--sf-variants-grid-more-background-hover);
        border-color: var(--sf-variants-grid-more-border-hover);
      }
    }
  }
</style>
